import React, {useEffect, useMemo, useState} from "react";

import {
    Button,
    Card,
    Col,
    Empty,
    Flex,
    message,
    Result,
    Row,
    Segmented,
    Spin,
    Statistic,
    Table,
    Tag,
    Tooltip,
} from "antd";
import {
    CalendarOutlined,
    DollarOutlined,
    FileExcelOutlined,
    ReloadOutlined,
    ShopOutlined,
    ShoppingCartOutlined,
} from "@ant-design/icons";

import Apiurl from "../ConstURL";
import axios from "axios";

const RU_MONTHS = [
    "", "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const monthKey = (m) => `${m.Year}-${m.Month}`;
const monthLabel = (m) => `${RU_MONTHS[m.Month] || "?"} ${m.Year}`;

const moneyFmt = new Intl.NumberFormat("ru-RU", {minimumFractionDigits: 2, maximumFractionDigits: 2});
const intFmt = new Intl.NumberFormat("ru-RU");

const fmtMoney = (v) => moneyFmt.format(v || 0);
const fmtInt = (v) => intFmt.format(v || 0);

// Подсветка: отрицательные значения красным, нулевые — приглушённо.
const moneyCell = (v) => {
    const num = Number(v) || 0;
    const color = num < 0 ? "#ff5c5c" : num === 0 ? "#6b6b78" : "#d3adf7";
    return <span style={{color, fontVariantNumeric: "tabular-nums"}}>{fmtMoney(num)}</span>;
};
const ordersCell = (v) => {
    const num = Number(v) || 0;
    const color = num < 0 ? "#ff5c5c" : num === 0 ? "#6b6b78" : "#e8e8ec";
    return <span style={{color, fontVariantNumeric: "tabular-nums"}}>{fmtInt(num)}</span>;
};

export default function NetSalesDashboard() {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [shops, setShops] = useState([]);
    const [months, setMonths] = useState([]);
    const [cellMap, setCellMap] = useState({});
    const [mode, setMode] = useState("net"); // net | gross
    const [exporting, setExporting] = useState(false);

    const loadData = () => {
        setLoading(true);
        setError(false);
        axios.get(Apiurl + "/dashboard/netsales?token=" + localStorage.getItem("token"))
            .then((res) => {
                const data = res.data;
                setShops(data.Shops || []);
                setMonths(data.Months || []);
                const map = {};
                (data.Cells || []).forEach((c) => {
                    map[`${c.ShopId}-${c.Year}-${c.Month}`] = c;
                });
                setCellMap(map);
                setLoading(false);
            })
            .catch((err) => {
                console.log(err);
                setError(true);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadData();
    }, []);

    const isNet = mode === "net";

    // Строки таблицы: магазины + итоговые суммы за всё время по строке.
    const dataSource = useMemo(() => {
        const net = mode === "net";
        return shops.map((shop) => {
            const row = {key: shop.Id, shop: shop.Name};
            let rowSales = 0;
            let rowOrders = 0;
            months.forEach((m) => {
                const c = cellMap[`${shop.Id}-${m.Year}-${m.Month}`];
                const sales = c ? (net ? c.NetSales : c.GrossSales) : 0;
                const orders = c ? (net ? c.NetOrders : c.GrossOrders) : 0;
                row[`${monthKey(m)}__s`] = sales;
                row[`${monthKey(m)}__o`] = orders;
                rowSales += sales;
                rowOrders += orders;
            });
            row.__totalSales = rowSales;
            row.__totalOrders = rowOrders;
            return row;
        });
    }, [shops, months, cellMap, mode]);

    // Колонки: фиксированный «Маркетплейс» + группа на каждый месяц + «Итого».
    const columns = useMemo(() => {
        const cols = [
            {
                title: "Маркетплейс",
                dataIndex: "shop",
                key: "shop",
                fixed: "left",
                width: 170,
                render: (v) => <b>{v}</b>,
            },
        ];
        months.forEach((m) => {
            cols.push({
                title: <span style={{whiteSpace: "nowrap"}}>{monthLabel(m)}</span>,
                key: monthKey(m),
                children: [
                    {
                        title: "Продажи",
                        dataIndex: `${monthKey(m)}__s`,
                        key: `${monthKey(m)}__s`,
                        align: "right",
                        width: 120,
                        render: moneyCell,
                    },
                    {
                        title: "Заказы",
                        dataIndex: `${monthKey(m)}__o`,
                        key: `${monthKey(m)}__o`,
                        align: "right",
                        width: 90,
                        render: ordersCell,
                    },
                ],
            });
        });
        cols.push({
            title: <span style={{whiteSpace: "nowrap"}}>Итого за всё время</span>,
            key: "total",
            fixed: "right",
            children: [
                {
                    title: "Продажи",
                    dataIndex: "__totalSales",
                    key: "__totalSales",
                    align: "right",
                    width: 130,
                    render: (v) => <b>{moneyCell(v)}</b>,
                },
                {
                    title: "Заказы",
                    dataIndex: "__totalOrders",
                    key: "__totalOrders",
                    align: "right",
                    width: 100,
                    render: (v) => <b>{ordersCell(v)}</b>,
                },
            ],
        });
        return cols;
    }, [months]);

    // Итоги по столбцам (нижняя строка) и общие показатели для карточек.
    const totals = useMemo(() => {
        const net = mode === "net";
        const perMonth = {};
        let grandSales = 0;
        let grandOrders = 0;
        months.forEach((m) => {
            let s = 0;
            let o = 0;
            shops.forEach((shop) => {
                const c = cellMap[`${shop.Id}-${m.Year}-${m.Month}`];
                s += c ? (net ? c.NetSales : c.GrossSales) : 0;
                o += c ? (net ? c.NetOrders : c.GrossOrders) : 0;
            });
            perMonth[monthKey(m)] = {s, o};
            grandSales += s;
            grandOrders += o;
        });
        return {perMonth, grandSales, grandOrders};
    }, [shops, months, cellMap, mode]);

    const downloadExcel = () => {
        setExporting(true);
        axios.get(Apiurl + "/dashboard/netsales/excel?token=" + localStorage.getItem("token"), {responseType: "blob"})
            .then((res) => {
                const url = window.URL.createObjectURL(res.data);
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute("download", res.headers["filename"] || "net_sales_dashboard.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                setExporting(false);
            })
            .catch((err) => {
                console.log(err);
                message.error("Не удалось скачать Excel");
                setExporting(false);
            });
    };

    const periodLabel = months.length
        ? `${monthLabel(months[0])} — ${monthLabel(months[months.length - 1])}`
        : "—";

    return (
        <div style={{padding: "24px 28px"}}>
            <Flex justify="flex-end" align="center" wrap="wrap" gap="16px"
                  style={{marginBottom: 20}}>
                <Flex gap="10px" wrap="wrap" align="center">
                    <Segmented
                        value={mode}
                        onChange={setMode}
                        options={[
                            {label: "Чистые продажи", value: "net"},
                            {label: "Валовые продажи", value: "gross"},
                        ]}
                    />
                    <Button icon={<ReloadOutlined/>} onClick={loadData}>Обновить</Button>
                    <Button type="primary" icon={<FileExcelOutlined/>} loading={exporting}
                            onClick={downloadExcel} disabled={loading || error}>
                        Excel
                    </Button>
                </Flex>
            </Flex>

            <Row gutter={[16, 16]} style={{marginBottom: 20}}>
                <Col xs={12} md={6}>
                    <Card size="small" variant="borderless" style={cardStyle}>
                        <Statistic
                            title={isNet ? "Чистые продажи (всего)" : "Валовые продажи (всего)"}
                            value={totals.grandSales}
                            precision={2}
                            prefix={<DollarOutlined/>}
                            valueStyle={{color: "#b37feb"}}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" variant="borderless" style={cardStyle}>
                        <Statistic
                            title={isNet ? "Чистые заказы (всего)" : "Заказы (всего)"}
                            value={totals.grandOrders}
                            prefix={<ShoppingCartOutlined/>}
                            valueStyle={{color: "#e8e8ec"}}
                        />
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" variant="borderless" style={cardStyle}>
                        <Statistic title="Маркетплейсов" value={shops.length}
                                   prefix={<ShopOutlined/>}/>
                    </Card>
                </Col>
                <Col xs={12} md={6}>
                    <Card size="small" variant="borderless" style={cardStyle}>
                        <div style={{color: "rgba(255,255,255,0.45)", fontSize: 14, marginBottom: 4}}>
                            <CalendarOutlined/> Период ({months.length} мес.)
                        </div>
                        <div style={{fontSize: 15, fontWeight: 600}}>{periodLabel}</div>
                    </Card>
                </Col>
            </Row>

            {error ? (
                <Result status="warning" title="Не удалось загрузить данные"
                        extra={<Button type="primary" onClick={loadData}>Повторить</Button>}/>
            ) : loading ? (
                <Flex justify="center" align="center" style={{minHeight: 300}}>
                    <Spin size="large" tip="Загрузка данных...">
                        <div style={{padding: 40}}/>
                    </Spin>
                </Flex>
            ) : months.length === 0 ? (
                <Empty description="Нет данных. Импортируйте отчёты по заказам и возвратам."/>
            ) : (
                <Table
                    size="small"
                    bordered
                    columns={columns}
                    dataSource={dataSource}
                    pagination={false}
                    scroll={{x: "max-content", y: "calc(100vh - 430px)"}}
                    rowClassName={(_, i) => (i % 2 ? "dash-row-odd" : "dash-row-even")}
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row style={{background: "#241b33"}}>
                                <Table.Summary.Cell index={0}>
                                    <b>Итого</b>
                                </Table.Summary.Cell>
                                {months.map((m, idx) => {
                                    const t = totals.perMonth[monthKey(m)] || {s: 0, o: 0};
                                    return (
                                        <React.Fragment key={monthKey(m)}>
                                            <Table.Summary.Cell index={1 + idx * 2} align="right">
                                                <b>{moneyCell(t.s)}</b>
                                            </Table.Summary.Cell>
                                            <Table.Summary.Cell index={2 + idx * 2} align="right">
                                                <b>{ordersCell(t.o)}</b>
                                            </Table.Summary.Cell>
                                        </React.Fragment>
                                    );
                                })}
                                <Table.Summary.Cell index={1 + months.length * 2} align="right">
                                    <b>{moneyCell(totals.grandSales)}</b>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={2 + months.length * 2} align="right">
                                    <b>{ordersCell(totals.grandOrders)}</b>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />
            )}

            <div style={{marginTop: 14}}>
                <Tooltip title="Чистые продажи = Σ item-price (статусы со словом Shipped, кроме Lost in Transit, Undeliverable, Rejected by Buyer, Label Cancelled, Returned/Returning to Seller) − Σ возвратов (Refunded Amount > 0); возвраты по заказам с этими статусами не вычитаются — заказ и так не вошёл в продажи. Заказы = уникальные отгруженные − уникальные возвращённые.">
                    <Tag color="purple">Методология расчёта</Tag>
                </Tooltip>
            </div>
        </div>
    );
}

const cardStyle = {
    background: "linear-gradient(135deg, #1f1830 0%, #181226 100%)",
    border: "1px solid #2e2440",
    borderRadius: 14,
};
